import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Mine / Clinic, on the home page.
 *
 * There used to be three overview surfaces — Home, Dashboard and Explore —
 * competing for the same job, which is how none of them got good. Dashboard
 * was never a different *place*; it was the same day at a different scope. So
 * it is a toggle, and only for the roles holding report:read (Owner, Clinic
 * Manager, Billing). Everyone else sees their own day with no chrome at all.
 *
 * Scope lives in the query string so /home?scope=clinic is linkable and the
 * old /dashboard bookmark can redirect straight onto it.
 */
export const HomeScopeSwitch: React.FC = () => {
  const { isRTL } = useTranslation();
  const [params, setParams] = useSearchParams();
  const scope = params.get('scope') === 'clinic' ? 'clinic' : 'mine';

  const set = (next: 'mine' | 'clinic') => {
    const q = new URLSearchParams(params);
    if (next === 'clinic') q.set('scope', 'clinic');
    else q.delete('scope');
    setParams(q, { replace: true });
  };

  const options: Array<{ key: 'mine' | 'clinic'; label: string }> = [
    { key: 'mine', label: isRTL ? 'أنا' : 'Mine' },
    { key: 'clinic', label: isRTL ? 'العيادة' : 'Clinic' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
      <div
        className="inline-flex items-center gap-1 rounded-lg p-1 border"
        role="tablist"
        aria-label={isRTL ? 'نطاق العرض' : 'View scope'}
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: 'var(--color-border-primary)',
        }}
      >
        {options.map((option) => {
          const active = option.key === scope;
          return (
            <button
              key={option.key}
              role="tab"
              aria-selected={active}
              onClick={() => set(option.key)}
              className="rounded-md px-3.5 py-1.5 text-[13px] font-semibold tracking-tight transition-colors"
              style={{
                backgroundColor: active
                  ? 'var(--color-bg-primary)'
                  : 'transparent',
                color: active
                  ? 'var(--color-text-primary)'
                  : 'var(--color-text-secondary)',
                boxShadow: active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none',
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default HomeScopeSwitch;
